import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aj_cart") || "[]"); } catch { return []; }
  });
  const first = useRef(true);

  useEffect(() => {
    localStorage.setItem("aj_cart", JSON.stringify(items));
    if (first.current) { first.current = false; return; }
    if (localStorage.getItem("aj_customer_token")) {
      api.put("/shop/cart", { items }).catch(() => {});
    }
  }, [items]);

  const add = (p, qty = 1, opts = {}) => {
    const price = p.group_price != null ? p.group_price : (p.effective_price != null ? p.effective_price : p.price);
    const isVolume = !!opts.volume;
    setItems((cur) => {
      const i = cur.findIndex((x) => x.product_id === p.id);
      if (i >= 0) {
        const c = [...cur];
        c[i] = { ...c[i], quantity: isVolume ? qty : c[i].quantity + qty };
        return c;
      }
      return [...cur, { product_id: p.id, slug: p.slug, name: p.name, price, image: p.image,
                        quantity: qty, is_volume: isVolume, unit_label: p.unit_label || "" }];
    });
  };
  const setQty = (id, qty) => setItems((cur) => cur.map((x) => (x.product_id === id ? { ...x, quantity: Math.max(1, qty) } : x)));
  const remove = (id) => setItems((cur) => cur.filter((x) => x.product_id !== id));
  const clear = () => setItems([]);

  // Merge the server-saved account cart with the local one (keep max quantity)
  const mergeAccountCart = async () => {
    try {
      const { data } = await api.get("/shop/cart");
      const server = data.items || [];
      setItems((cur) => {
        const map = new Map(cur.map((x) => [x.product_id, x]));
        for (const s of server) {
          const ex = map.get(s.product_id);
          if (ex) map.set(s.product_id, { ...ex, quantity: Math.max(ex.quantity, s.quantity) });
          else map.set(s.product_id, s);
        }
        return Array.from(map.values());
      });
    } catch { /* ignore */ }
    await repriceCart();
  };

  // Re-price cart items against current (group-aware) product prices
  const repriceCart = async () => {
    try {
      const { data } = await api.get("/products", { params: { active_only: false } });
      const map = new Map(data.map((p) => [p.id, p]));
      setItems((cur) => cur.map((x) => {
        const p = map.get(x.product_id);
        if (!p) return x;
        const price = p.group_price != null ? p.group_price : (p.effective_price != null ? p.effective_price : p.price);
        return { ...x, price };
      }));
    } catch { /* ignore */ }
  };

  const subtotal = items.reduce((s, x) => s + x.price * x.quantity, 0);
  const count = items.reduce((s, x) => s + (x.is_volume ? 1 : x.quantity), 0);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, subtotal, count, mergeAccountCart, repriceCart }}>
      {children}
    </CartContext.Provider>
  );
}
